/**
 * Figure S5's "Disclosures (N) · masked exactly as <Entity> sees them".
 *
 * The ladders this entity is on, shown exactly as the entity itself sees them:
 * a counterparty is a name only from rung 4, after a mutual NDA, and never
 * before. The portfolio watches; only the entity can consent.
 */
import { Link } from 'react-router-dom'
import RungMeter from './RungMeter'
import { DISCLOSURE_STATUS_TONE } from './disclosureRows'
import type { DisclosureRow } from './disclosureRows'

export interface EntityDisclosuresProps {
  rows: DisclosureRow[]
  entityLabel: string
}

export default function EntityDisclosures({ rows, entityLabel }: EntityDisclosuresProps) {
  return (
    <div data-part="EntityDisclosures" className="light-card p-4">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="flex items-baseline gap-2 flex-wrap">
          <h3 className="text-[15px] font-bold text-iflow-900">Disclosures ({rows.length})</h3>
          <span className="text-[12px] text-gray-500">· masked exactly as {entityLabel} sees them</span>
        </div>
        <Link to="/agg/across" className="text-[12px] font-semibold text-iflow-700 hover:text-iflow-800 hover:underline">
          Open Across portfolios →
        </Link>
      </div>

      {rows.length === 0 ? (
        <p className="mt-3 text-[12.5px] text-gray-600">
          {entityLabel} is not on any disclosure ladder yet.
        </p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-left text-[12.5px]">
            <thead>
              <tr className="bg-iflow-900 text-white">
                <th className="rounded-l-lg px-3 py-2 font-semibold">Counterparty</th>
                <th className="px-3 py-2 font-semibold">Rung</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="rounded-r-lg px-3 py-2 font-semibold">Pending action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.agreement.agreement_id}
                  data-testid="entity-disclosure-row"
                  className="border-b border-iflow-900/5"
                >
                  <td className="px-3 py-2 align-top">
                    <b className="text-iflow-900">{row.counterpartyLabel}</b>
                    {row.counterpartyNote && (
                      <div className="text-[11.5px] text-gray-500">{row.counterpartyNote}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <RungMeter level={row.level} ndaPending={row.ndaPending} caption={row.rungCaption} />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${DISCLOSURE_STATUS_TONE[row.status]}`}
                    >
                      {row.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 align-top text-gray-500">{row.pendingAction}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 text-[11.5px] text-gray-500">
        Only the entity can consent — you can watch, never advance.
      </p>
    </div>
  )
}
